import { useState } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import type {
  DecisionChoice,
  MergeDecisionDto,
  MergePeriodDto,
  MergeRowDto,
} from '../api/mergeResolution.types';
import { periodKey } from '../state/cellKey';
import { validateCustomValue } from '../state/customValue';
import type { CellKey } from '../state/types';
import { detailToClipboardText } from '../utils/clipboardText';
import { variancePctStyle } from '../utils/varianceTone';

import type { DetailPane } from './ConflictGrid';
import {
  CELL_PADDING_X,
  FROZEN_WIDTH_VAR,
  periodWidthVar,
} from './gridColumns';

const EM_DASH = '—';

interface RowDetailProps {
  row: MergeRowDto;
  headers: string[];
  label: (dateId: string | null) => string;
  decisions: Record<CellKey, MergeDecisionDto>;
  onCustom: (period: MergePeriodDto, value: string) => void;
  onClear: (period: MergePeriodDto) => void;
  /**
   * Drops the decision on a cell so it returns to its auto value. Used for the
   * ✕ on an override of a cell that is not a conflict. Omitted, that ✕ is not
   * offered.
   */
  onRevert?: (period: MergePeriodDto) => void;
  legal: (period: MergePeriodDto) => DecisionChoice[];
  /**
   * The grid's period columns, in the grid's own order. Given these, the
   * detail emits one cell per column — including an empty one where this row
   * has no period for that date — so its columns line up with the grid's
   * instead of being packed from whatever periods this row happens to carry.
   *
   * Optional, and omitting it falls back to this row's own periods, which is
   * what this component did before it was asked to align.
   */
  periodKeys?: (string | null)[];
  /**
   * Width of the metric-label column, set to the grid's frozen width so the
   * labels sit under the frozen identity block and the first value cell
   * begins at the freeze boundary.
   */
  labelWidth?: number;
  /** Width of one period cell, matching the grid's period columns. */
  periodWidth?: number;
  /**
   * Which half of the split detail row to render. The grid renders the
   * expanded row once per column container so each half scrolls with its own
   * columns, so `frozen` emits the metric labels and the row's chrome and
   * `scrolling` emits the period cells.
   *
   * Omitted renders both halves in one table, which is what this component did
   * before the row was split and is what its unit tests exercise.
   */
  pane?: DetailPane;
}

type MetricKey = 'base' | 'theirs' | 'mine' | 'variance' | 'variancePct';

const METRIC_ROWS: Array<{ key: MetricKey; label: string }> = [
  { key: 'base', label: 'Base' },
  { key: 'theirs', label: 'Theirs' },
  { key: 'mine', label: 'Mine' },
  // The formulas are the API's: Raw_Difference is Theirs - Mine and
  // Pct_Difference is that difference over Mine.
  { key: 'variance', label: 'Variance (Theirs − Mine)' },
  { key: 'variancePct', label: 'Variance % (Variance ÷ Mine)' },
];

// Current "Mine" value for a period: a CUSTOM decision overrides it, a CLEAR
// decision blanks it, anything else (including no decision) shows the
// period's own Mine value.
const mineValue = (
  period: MergePeriodDto,
  decision: MergeDecisionDto | undefined
): string | null => {
  if (decision?.choice === 'CUSTOM') {
    return decision.custom_value ?? null;
  }
  if (decision?.choice === 'CLEAR') {
    return null;
  }
  return period.Mine;
};

const display = (value: string | null): string => value ?? EM_DASH;

/** Variance % to one decimal; a non-numeric wire string is shown as received. */
const formatPct = (pct: string | null): string => {
  if (pct === null) {
    return EM_DASH;
  }
  const asNumber = Number(pct);
  return Number.isFinite(asNumber) ? `${asNumber.toFixed(1)}%` : `${pct}%`;
};

/**
 * Whether two wire values are the same number.
 *
 * The wire carries values as strings, so a plain equality check would call
 * '25' and '25.00' different and offer a Clear button on a cell that has not
 * actually moved. Numeric comparison when both sides parse, string equality
 * otherwise (an Uptake/Event curve value need not be numeric).
 */
const sameValue = (a: string | null, b: string | null): boolean => {
  if (a === b) {
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) {
    return na === nb;
  }
  return false;
};

/**
 * Fixed geometry, because the two halves of a split detail row are laid out
 * independently and have to agree on where every row starts.
 *
 * The chrome strip is rendered at this height in BOTH halves even though only
 * the frozen half puts anything in it — otherwise the five metric rows would
 * start at different heights on either side of the freeze. Same reason the
 * metric rows get an explicit height and zero vertical padding: the Mine cell
 * carries an icon button and would otherwise be taller than its own label.
 */
const CHROME_HEIGHT = 36;
const METRIC_ROW_HEIGHT = 44;

// Keep in step with `--ag-font-size` in conflictGrid.scss, so the expanded
// row reads at the same size as the grid above it.
const GRID_FONT_SIZE = '12px';

/**
 * Left inset on the expanded panel, so it reads as nested under its row rather
 * than flush with the grid. Roughly half a centimetre at 96dpi.
 *
 * Applied to the frozen half only, and taken back out of the metric-label
 * column's width. The scrolling half starts at the freeze boundary in its own
 * container, and indenting it would push every value out of line with the
 * period column above it - which is the alignment this panel exists to keep.
 */
const EXPANSION_INDENT = 18;

/** Horizontal padding inside the box drawn around an editable Mine value. */
const EDIT_BOX_PADDING_X = 4;

const RowDetail = ({
  row,
  headers,
  label,
  decisions,
  onCustom,
  onClear,
  onRevert,
  legal,
  periodKeys,
  labelWidth,
  periodWidth,
  pane,
}: RowDetailProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [editingKey, setEditingKey] = useState<CellKey | null>(null);
  const [draftValue, setDraftValue] = useState('');
  const [draftError, setDraftError] = useState<string | undefined>();

  const dimensionPath = row.Dimensions.map(
    (value, i) => `${headers[i] ?? `Dimension ${i + 1}`}: ${value}`
  ).join(' / ');

  const handleCopy = async () => {
    await navigator.clipboard.writeText(
      detailToClipboardText(row, headers, label, periodKeys)
    );
    enqueueSnackbar('Copied');
  };

  const startEdit = (period: MergePeriodDto, decision?: MergeDecisionDto) => {
    if (!legal(period).includes('CUSTOM')) {
      return;
    }
    setEditingKey(periodKey(row, period));
    setDraftValue(mineValue(period, decision) ?? '');
    setDraftError(undefined);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraftValue('');
    setDraftError(undefined);
  };

  const commitEdit = (period: MergePeriodDto) => {
    const result = validateCustomValue(draftValue);
    if (!result.ok) {
      setDraftError(result.reason);
      return;
    }
    onCustom(period, result.value);
    cancelEdit();
  };

  // The columns this detail renders, one per grid period column. `undefined`
  // means the grid has a column for that date but this row has no period
  // there, which renders as an empty cell rather than shifting everything to
  // its left.
  const columns: {
    key: string;
    dateId: string | null;
    period: MergePeriodDto | undefined;
  }[] = (periodKeys ?? row.Periods.map((period) => period.DateID)).map(
    (dateId, i) => ({
      key: dateId ?? `period-${i}`,
      dateId,
      period: row.Periods.find((period) => period.DateID === dateId),
    })
  );

  const showLabels = pane !== 'scrolling';
  const showPeriods = pane !== 'frozen';

  // Only the half that starts at the grid's left edge gets the inset.
  const indent = pane === 'scrolling' ? 0 : EXPANSION_INDENT;

  // One column's cell geometry. The width is read from the custom property the
  // grid publishes for that column (see `FROZEN_WIDTH_VAR`), so it follows a
  // resize with no re-render; `periodWidth` is the fallback until the grid has
  // published one.
  const periodCellSxFor = (dateId: string | null) => {
    const width = periodWidth
      ? `var(${periodWidthVar(dateId)}, ${periodWidth}px)`
      : undefined;
    return {
      py: 0,
      px: `${CELL_PADDING_X}px`,
      height: METRIC_ROW_HEIGHT,
      ...(width ? { width, minWidth: width, maxWidth: width } : {}),
    };
  };

  // The inset comes out of the label column, so the first value cell still
  // lands exactly on the freeze boundary.
  const labelColumnWidth = labelWidth
    ? `calc(var(${FROZEN_WIDTH_VAR}, ${labelWidth}px) - ${indent}px)`
    : undefined;
  const labelCellSx = {
    py: 0,
    px: `${CELL_PADDING_X}px`,
    height: METRIC_ROW_HEIGHT,
    ...(labelColumnWidth
      ? {
          width: labelColumnWidth,
          minWidth: labelColumnWidth,
          maxWidth: labelColumnWidth,
        }
      : {}),
  };

  const renderMineCell = (period: MergePeriodDto, i: number) => {
    const key = periodKey(row, period);
    const decision = decisions[key];
    const legalChoices = legal(period);
    const canCustom = legalChoices.includes('CUSTOM');
    const canClear = legalChoices.includes('CLEAR');
    const isEditing = editingKey === key;

    // Clear is offered only when Mine actually departs from Base - there is
    // nothing to clear back to otherwise, and an always-present button put a
    // control on every cell in the row whether or not it did anything.
    // An override of a cell with no conflict is undone, not blanked: there is
    // no choice to fall back to, so the ✕ drops the decision instead.
    const isOverride =
      period.Class !== 'CONFLICT' &&
      (decision?.choice === 'CUSTOM' || decision?.choice === 'CLEAR');
    const showRevert = isOverride && onRevert !== undefined;
    const showClear =
      !isOverride &&
      canClear &&
      !sameValue(mineValue(period, decision), period.Base);

    return (
      <TableCell
        key={period.DateID ?? `period-${i}`}
        align="right"
        sx={periodCellSxFor(period.DateID)}
      >
        {/*
          A flex row with Clear FIRST and the value taking the remaining space,
          right-aligned. The order matters: the values are right-aligned, so a
          button after the number would push it left whenever it appeared and
          break the column. Ahead of it, the number's right edge stays at the
          cell's right padding whether or not the button is there.
        */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {showRevert && (
            <IconButton
              size="small"
              aria-label="Clear override"
              onClick={() => onRevert?.(period)}
            >
              <ClearIcon fontSize="inherit" />
            </IconButton>
          )}
          {showClear && (
            <IconButton
              size="small"
              aria-label="Clear"
              onClick={() => onClear(period)}
            >
              <ClearIcon fontSize="inherit" />
            </IconButton>
          )}
          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
            {isEditing ? (
              <TextField
                autoFocus
                size="small"
                value={draftValue}
                error={Boolean(draftError)}
                helperText={draftError}
                onChange={(e) => setDraftValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    commitEdit(period);
                  } else if (e.key === 'Escape') {
                    cancelEdit();
                  }
                }}
              />
            ) : (
              <Box
                component="span"
                onClick={() => startEdit(period, decision)}
                sx={
                  canCustom
                    ? {
                        // Boxed like an input so it reads as editable. The
                        // negative margin takes back the box's own padding and
                        // border, so the number's right edge stays on the
                        // column's like every other row's.
                        display: 'inline-block',
                        minWidth: 48,
                        px: `${EDIT_BOX_PADDING_X}px`,
                        py: '3px',
                        mr: `-${EDIT_BOX_PADDING_X + 1}px`,
                        border: '1px solid',
                        borderColor: 'grey.400',
                        borderRadius: '4px',
                        backgroundColor: 'common.white',
                        cursor: 'text',
                        '&:hover': { borderColor: 'primary.main' },
                      }
                    : { cursor: 'default' }
                }
              >
                {display(mineValue(period, decision))}
              </Box>
            )}
          </Box>
        </Box>
      </TableCell>
    );
  };

  const renderCell = (metric: MetricKey, period: MergePeriodDto, i: number) => {
    if (metric === 'mine') {
      return renderMineCell(period, i);
    }

    if (metric === 'variancePct') {
      const pct = period.Pct_Difference;
      return (
        <TableCell
          key={period.DateID ?? `period-${i}`}
          align="right"
          sx={{ ...variancePctStyle(pct), ...periodCellSxFor(period.DateID) }}
        >
          {formatPct(pct)}
        </TableCell>
      );
    }

    const value =
      metric === 'base'
        ? period.Base
        : metric === 'theirs'
          ? period.Theirs
          : period.Raw_Difference;

    return (
      <TableCell
        key={period.DateID ?? `period-${i}`}
        align="right"
        sx={periodCellSxFor(period.DateID)}
      >
        {display(value)}
      </TableCell>
    );
  };

  return (
    <Box sx={{ pl: `${indent}px` }}>
      {/*
        Held at a fixed height in both halves of a split row, so the metric
        rows below start at the same place on either side of the freeze. Only
        the frozen half fills it.
      */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          height: CHROME_HEIGHT,
          overflow: 'hidden',
        }}
      >
        {showLabels && (
          <>
            <Box sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              {dimensionPath}
            </Box>
            <Tooltip title="Copy to Clipboard">
              <IconButton
                size="small"
                aria-label="Copy to Clipboard"
                onClick={handleCopy}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
      {/*
        No date header row. The periods are already labelled by the grid's own
        period column headers directly above, and repeating them here both
        duplicated the information and, because this table packed its cells
        from `row.Periods`, labelled them in a different place from the grid.
        `tableLayout: fixed` is what makes the explicit widths bind.
      */}
      <Table
        size="small"
        aria-label={`Row detail for ${dimensionPath}`}
        sx={{
          tableLayout: 'fixed',
          width: 'auto',
          '& .MuiTableCell-root': { fontSize: GRID_FONT_SIZE },
        }}
      >
        <TableBody>
          {METRIC_ROWS.map((metric) => (
            <TableRow key={metric.key} sx={{ height: METRIC_ROW_HEIGHT }}>
              {showLabels && (
                <TableCell component="th" scope="row" sx={labelCellSx}>
                  {metric.label}
                </TableCell>
              )}
              {showPeriods &&
                columns.map(({ key, dateId, period }, i) =>
                  period ? (
                    renderCell(metric.key, period, i)
                  ) : (
                    <TableCell key={key} sx={periodCellSxFor(dateId)} />
                  )
                )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default RowDetail;
